import { Girando, Selo, cx } from "../../components/ui.js";
import { lerImpressora, quandoFoiVerificada, type ImpressoraDaTela } from "./impressora.js";
import { comVerbo } from "./tempo.js";

/**
 * O estado da impressora, para quem opera: um selo ("Pronta", "Sem contato"…), a frase que explica, e de
 * quando é a informação.
 *
 * É `aria-live` porque a verificação acontece sozinha ao abrir o painel e de novo no "Verificar agora":
 * quem usa leitor de tela precisa ouvir que mudou de "Verificando…" para "Pronta".
 *
 * Pelo agente do notebook (#109), o estado é o que o agente viu, e só vale com ele de pé: desligado, a tela diz
 * isso e de quando é o último sinal dele.
 */
export function EstadoDaImpressora({
  estado,
  estadoEm,
  conexao,
  agente,
  verificando,
  agora,
}: {
  estado: ImpressoraDaTela["estado"];
  estadoEm: string | null;
  conexao?: ImpressoraDaTela["conexao"];
  agente?: ImpressoraDaTela["agente"];
  verificando: boolean;
  agora: Date;
}) {
  const leitura = lerImpressora({ estado, conexao, agente });
  const quando =
    leitura.situacao === "agente-desligado"
      ? agente?.vistoEm
        ? comVerbo("Último sinal", agente.vistoEm, agora)
        : null
      : leitura.situacao === "sem-agente" || estado === null
        ? null
        : quandoFoiVerificada(estadoEm, agora);

  return (
    <div aria-live="polite" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        {verificando ? (
          <Selo tom="projecao">
            <Girando className="size-3" />
            Verificando…
          </Selo>
        ) : (
          <Selo tom={leitura.tom}>{leitura.rotulo}</Selo>
        )}
        {!verificando && quando && (
          <span className="text-[0.75rem] text-texto-suave">{quando}</span>
        )}
      </div>

      {!verificando && leitura.detalhe && (
        <p
          className={cx(
            "text-[0.875rem] leading-relaxed wrap-anywhere",
            // A frase de "sem contato" explica a rede e o que fazer: é a que precisa ser lida.
            leitura.situacao === "sem-contato" ||
              leitura.situacao === "parada" ||
              leitura.situacao === "agente-desligado"
              ? "text-texto"
              : "text-texto-suave",
          )}
        >
          {leitura.detalhe}
        </p>
      )}

      {!verificando &&
        leitura.avisos.map((aviso) => (
          <p key={aviso} className="text-[0.8125rem] leading-relaxed text-brasa">
            {aviso}
          </p>
        ))}
    </div>
  );
}
